import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import axios from 'axios';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api';

interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  paid: boolean;
  date: string;
  periodStart: string | null;
  periodEnd: string | null;
  description: string;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
  subscriptionId: string | null;
}

interface SubscriptionDetails {
  id?: string;
  status: string;
  plan: string | { name: string; amount: number; currency: string; interval: string };
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

const Billing: React.FC = () => {
  const { user } = useContext(UserContext);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');

        if (!token) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        // Fetch subscription details and billing history in parallel
        const [subscriptionResponse, invoicesResponse] = await Promise.all([
          axios.get(`${API_URL}/subscription/subscription-details`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/subscription/billing-history`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (subscriptionResponse.data.success && subscriptionResponse.data.hasActiveSubscription) {
          setSubscription(subscriptionResponse.data.subscription);
        } else {
          // Use user's subscription from context if no Stripe subscription
          setSubscription({
            status: user?.subscription?.status || 'none',
            plan: user?.subscription?.plan || 'none',
            currentPeriodEnd: user?.subscription?.currentPeriodEnd
          });
        }

        if (invoicesResponse.data.success) {
          setInvoices(invoicesResponse.data.invoices);
        }
      } catch (err: any) {
        console.error('Error fetching billing data:', err);
        setError(err.response?.data?.error || 'Failed to load billing information');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [user]);

  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; border: string }> = {
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      open: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      void: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
      uncollectible: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' }
    };

    const config = statusConfig[status] || statusConfig.open;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPlanName = (plan: string | { name: string } | undefined): string => {
    if (!plan || plan === 'none') return 'Free';
    if (typeof plan === 'object' && plan.name) return plan.name;
    if (typeof plan === 'string') {
      const planLower = plan.toLowerCase();
      if (planLower.includes('premium') || planLower.includes('pro') || planLower.includes('basic')) {
        return 'Premium';
      }
      return plan;
    }
    return 'Free';
  };

  const pageBackground = { background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col" style={pageBackground}>
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={pageBackground}>
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Subscription</h1>
          <p className="text-white/70">Manage your subscription and view billing history</p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Subscription Details Card */}
          <div className="lg:col-span-1">
            <div 
              className="rounded-2xl p-6 border border-white/10"
              style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
            >
              <h2 className="text-xl font-semibold text-white mb-4">Subscription Details</h2>
              
              {subscription && subscription.status === 'active' ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">Plan</p>
                    <p className="text-white font-semibold text-lg">
                      {getPlanName(subscription.plan)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-white/60 text-sm mb-1">Status</p>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold border bg-green-500/20 text-green-400 border-green-500/30">
                      Active
                    </span>
                  </div>

                  {subscription.currentPeriodEnd && (
                    <div>
                      <p className="text-white/60 text-sm mb-1">Renews On</p>
                      <p className="text-white font-medium">
                        {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  )}

                  {subscription.currentPeriodStart && (
                    <div>
                      <p className="text-white/60 text-sm mb-1">Current Period</p>
                      <p className="text-white text-sm">
                        {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd || undefined)}
                      </p>
                    </div>
                  )}

                  {typeof subscription.plan === 'object' && subscription.plan.amount && (
                    <div>
                      <p className="text-white/60 text-sm mb-1">Amount</p>
                      <p className="text-white font-semibold">
                        {formatCurrency(subscription.plan.amount, subscription.plan.currency || 'USD')} / {subscription.plan.interval}
                      </p>
                    </div>
                  )}

                  {subscription.cancelAtPeriodEnd && (
                    <div className="mt-4 p-3 rounded-lg bg-yellow-500/20 border border-yellow-500/30">
                      <p className="text-yellow-400 text-sm">
                        ⚠️ Subscription will cancel at the end of the current period
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-white/70 mb-4">No active subscription</p>
                  <Link
                    to="/payment"
                    className="btn-adsmash-primary inline-block px-6 py-2 rounded-lg text-white font-semibold transition hover:opacity-90"
                  >
                    Upgrade to Premium
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Billing History Card */}
          <div className="lg:col-span-2">
            <div 
              className="rounded-2xl p-6 border border-white/10"
              style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
            >
              <h2 className="text-xl font-semibold text-white mb-4">Billing History</h2>
              
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/70 mb-4">No billing history found</p>
                  <p className="text-white/50 text-sm">
                    Your invoices will appear here once you make a payment
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-white/70 text-sm font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-white/70 text-sm font-medium">Description</th>
                        <th className="text-left py-3 px-4 text-white/70 text-sm font-medium">Amount</th>
                        <th className="text-left py-3 px-4 text-white/70 text-sm font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-white/70 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5 transition">
                          <td className="py-4 px-4 text-white text-sm">
                            {formatDate(invoice.date)}
                          </td>
                          <td className="py-4 px-4 text-white text-sm">
                            {invoice.description}
                            {invoice.number && (
                              <span className="text-white/50 block text-xs mt-1">
                                Invoice #{invoice.number}
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-white font-medium">
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(invoice.status)}
                          </td>
                          <td className="py-4 px-4">
                            {invoice.hostedInvoiceUrl && (
                              <a
                                href={invoice.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition"
                              >
                                View Invoice
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Billing;

